import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, EMPTY, filter, map, switchMap, withLatestFrom } from 'rxjs';

import { LayoutStoreFacade } from './layout-store.facade';
import * as LayoutActions from './layout.actions';
import { ProcessStatuses } from '../../../definitions/enums/replenishment-process/process-status';
import { ProcessTypeChangeReason } from '../../../definitions/enums/replenishment-process/process-type-change-reasons.enum';
import { ProcessType } from '../../../definitions/enums/replenishment-process/process-type.enum';
import { UiConfig } from '../../../definitions/interfaces/layout/configs/ui-config';
import * as AdminPanelActions from '../../../pages/replenishment/admin-panel-page/feature-store/admin-panel.actions';
import { ReplenishmentService } from '../../../pages/replenishment/create-process-page/services/replenishment.service';
import * as CustomerOverviewActions from '../../../pages/replenishment/customer-overview-page/feature-state/customer-overview.actions';
import { CustomerService } from '../../../services/customer/customer.service';
import { NotificationsService } from '../../../services/notifications/notifications.service';
import { UserService } from '../../../services/user/user.service';
import { CommonMethodsService } from '../../../utils/common-methods/common-methods.service';
import { UserConfigsStoreFacade } from '../user-configs/user-configs-store.facade';
import * as UserConfigsActions from '../user-configs/user-configs.actions';

@Injectable()
export class LayoutEffects {
  private readonly actions$ = inject(Actions);
  private readonly userConfigFacade = inject(UserConfigsStoreFacade);
  private readonly userService = inject(UserService);
  private readonly replenishmentService = inject(ReplenishmentService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly layoutStoreFacade = inject(LayoutStoreFacade);
  private customerService = inject(CustomerService);

  public loadApprovalsRequiredCount = createEffect(() => this.actions$.pipe(
    ofType(UserConfigsActions.replenishmentStarted, LayoutActions.processCountsChange),
    switchMap(() => this.replenishmentService.getReplenishmentsCount(
      CommonMethodsService.prepareFiltersBase64String({ status: [ ProcessStatuses.APPROVAL_REQUIRED ]})
    ).pipe(
      map((approvalsRequiredCount: number) => LayoutActions.approvalsRequiredCountLoaded({ approvalsRequiredCount })),
      catchError(() => EMPTY))
    )));

  public loadNewPurchasesCount = createEffect(() => this.actions$.pipe(
    ofType(UserConfigsActions.replenishmentStarted, LayoutActions.processCountsChange),
    switchMap(() => this.replenishmentService.getReplenishmentsCount(
      CommonMethodsService.prepareFiltersBase64String({ status: [ ProcessStatuses.NEW ], type: [ ProcessType.PURCHASE ]})
    ).pipe(
      map((newPurchasesCount: number) => LayoutActions.newPurchasesCountLoaded({ newPurchasesCount })),
      catchError(() => EMPTY))
    )));

  public loadUnreadNotificationsCount$ = createEffect(() => this.actions$.pipe(
    ofType(UserConfigsActions.replenishmentStarted, LayoutActions.unreadNotificationsCount.load),
    switchMap(() => this.notificationsService.getUnreadNotificationsCount().pipe(
      map((count: number) => LayoutActions.unreadNotificationsCount.loadSuccessfully({ count }))
    ))
  ));

  public loadUiConfigs$ = createEffect(() => this.actions$.pipe(
    ofType(UserConfigsActions.replenishmentStarted),
    withLatestFrom(this.userConfigFacade.userId$),
    switchMap(([ _, userId ]: [unknown, string | undefined ]) =>
      this.userService.getUiConfigs(userId!).pipe(
        map(({ payload }: { payload: Array<UiConfig> }) => LayoutActions.uiConfigsLoaded({ uiConfigs: payload }))
      ))
  ));

  public updateUiConfig$ = createEffect(() => this.actions$.pipe(
    ofType(LayoutActions.uiConfigChanged),
    withLatestFrom(
      this.userConfigFacade.userId$,
    ),
    switchMap(([{ uiConfig }, userId ]: [ { uiConfig: UiConfig }, string | undefined ]) =>
      this.userService.updateUiConfig(userId!, uiConfig).pipe(
        map(() => LayoutActions.uiConfigUpdated({ uiConfig }))
      )
    )
  ));

  public loadTypeChangeReasons$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LayoutActions.changeTypeReasons.load),
      filter(() => !this.layoutStoreFacade.changeTypeReasonsSignal().length),
      switchMap(() => this.replenishmentService.getChangeTypeReasons().pipe(
        map((reasons: Array<ProcessTypeChangeReason>) => LayoutActions.changeTypeReasons.loadSuccessfully({ reasons }))
      ))
    );
  });

  public clearCustomerCache$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        CustomerOverviewActions.updateApprovalManagement.updateSuccess,
        AdminPanelActions.updateCustomerSuccess
      ),
      switchMap(() => this.customerService.clearCustomerCache())
    );
  }, { dispatch: false });
}
